/**
 * Data layer for the per-attendee Event Experience Report (ADR-136).
 *
 * Single entry point `loadAttendeeReport(eventId, personId)`. Per ADR-136
 * identity is person-keyed, with the Auth uid hanging off
 * `persons.authUid` (null until claimed). The report unifies the prior
 * dual-path "active user vs staged contact" services into one resolver:
 *
 *   1. Load the person + their event participation.
 *   2. Determine subject audience set via
 *      `audiences.where('resolvedPersonIds','array-contains',personId)`
 *      — this works for both claimed and pending people, no branching.
 *   3. Fetch consumer docs (sessions, travelItems, notifications) via
 *      `audienceId in [...subjectAudienceIds]`.
 *   4. Receipts: `notificationReceipts.where('personId','==',personId)`.
 *      Pending people generally have no receipts yet; ones written for
 *      personId pre-claim are picked up here too.
 */

import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import {
  chunkArray,
  personalAudienceId,
  type Audience,
  type EventParticipant,
  type Notification,
  type NotificationReceipt,
  type Person,
  type PersonProfile,
  type Role,
  type Session,
  type Survey,
  type TravelItem,
} from "@event-planner/shared";
import { db } from "../lib/firebase";
import type { TraceMembershipIndex } from "../utils/traceMembership";

/** Firestore `in` operator hard cap. */
const IN_CHUNK = 30;

export interface AttendeeReportSubject {
  personId: string;
  person: Person;
  profile: PersonProfile | null;
  participant: EventParticipant | null;
  /** True when `person.authUid` is set (claim has happened). */
  isClaimed: boolean;
}

export interface ReportNotificationEntry {
  notification: Notification;
  /** Null for unclaimed subjects (no receipt yet) and for scheduled/draft items. */
  receipt: NotificationReceipt | null;
  /**
   * - `delivered`: receipt exists with delivery timestamp.
   * - `pending_activation`: pending subject (authUid null) — matched by
   *   audience but no receipt yet; receipts backfill on activation.
   * - `queued`: notification is in `draft` / `scheduled` / `cancelled`,
   *   no receipt yet by definition.
   */
  deliveryState: "delivered" | "pending_activation" | "queued";
}

export interface AttendeeReportData {
  subject: AttendeeReportSubject;
  audiences: Audience[];
  sessions: Session[];
  travelItems: TravelItem[];
  notifications: ReportNotificationEntry[];
  surveys: Survey[];
  traceIndex: TraceMembershipIndex;
}

// ── helpers ──────────────────────────────────────────────────────────────

function uniqById<T extends { id: string }>(rows: T[]): T[] {
  const seen = new Map<string, T>();
  for (const row of rows) if (!seen.has(row.id)) seen.set(row.id, row);
  return Array.from(seen.values());
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

async function fetchDocsByIdsChunked<T extends { id: string }>(
  collectionName: string,
  ids: string[],
): Promise<T[]> {
  if (ids.length === 0) return [];
  const out: T[] = [];
  for (const chunk of chunkArray(ids, IN_CHUNK)) {
    const snap = await getDocs(
      query(collection(db, collectionName), where(documentId(), "in", chunk)),
    );
    for (const d of snap.docs) {
      out.push({ id: d.id, ...(d.data() as Omit<T, "id">) } as T);
    }
  }
  return out;
}

async function fetchByEventAndAudiences<T extends { id: string }>(
  collectionName: string,
  eventId: string,
  audienceIds: string[],
): Promise<T[]> {
  if (audienceIds.length === 0) return [];
  const out: T[] = [];
  for (const chunk of chunkArray(audienceIds, IN_CHUNK)) {
    const snap = await getDocs(
      query(
        collection(db, collectionName),
        where("eventId", "==", eventId),
        where("audienceId", "in", chunk),
      ),
    );
    for (const d of snap.docs) {
      out.push({ id: d.id, ...(d.data() as Omit<T, "id">) } as T);
    }
  }
  return uniqById(out);
}

async function fetchSurveysByNotificationIds(
  eventId: string,
  notificationIds: string[],
): Promise<Survey[]> {
  if (notificationIds.length === 0) return [];
  const out: Survey[] = [];
  for (const chunk of chunkArray(notificationIds, IN_CHUNK)) {
    const snap = await getDocs(
      query(
        collection(db, "surveys"),
        where("eventId", "==", eventId),
        where("notificationId", "in", chunk),
      ),
    );
    for (const d of snap.docs) {
      out.push({ id: d.id, ...(d.data() as Omit<Survey, "id">) } as Survey);
    }
  }
  return uniqById(out);
}

/**
 * Collect the audience ids referenced by every consumer drawn in the
 * report — including audiences that compose the consumer's `audienceId`
 * via `members.includedAudienceIds`. The trace helper needs all of them
 * in its index to classify anonymous compositions.
 */
function collectReferencedAudienceIds(
  consumers: { audienceId: string }[],
  alreadyLoaded: Map<string, Audience>,
): string[] {
  const referenced = new Set<string>();
  for (const c of consumers) referenced.add(c.audienceId);
  for (const aud of alreadyLoaded.values()) {
    for (const inc of aud.members.includedAudienceIds) referenced.add(inc);
  }
  const missing: string[] = [];
  for (const id of referenced) if (!alreadyLoaded.has(id)) missing.push(id);
  return missing;
}

async function backfillReferencedAudiences(
  consumers: { audienceId: string }[],
  audiencesById: Map<string, Audience>,
): Promise<void> {
  // Do up to five passes to follow `includedAudienceIds` into anonymous
  // compositions; the resolver caps composition depth at 5 but typical
  // reports only need 1-2 hops to fully populate.
  for (let pass = 0; pass < 5; pass++) {
    const missing = collectReferencedAudienceIds(consumers, audiencesById);
    if (missing.length === 0) return;
    const fetched = await fetchDocsByIdsChunked<Audience>("audiences", missing);
    for (const a of fetched) audiencesById.set(a.id, a);
    if (fetched.length === 0) return; // some refs are stale; stop
  }
}

function buildTraceIndex(
  audiencesById: Map<string, Audience>,
  personalId: string,
  personId: string,
  subjectRole: Role | null,
): TraceMembershipIndex {
  const subjectAudienceIds = new Set<string>();
  subjectAudienceIds.add(personalId);
  for (const aud of audiencesById.values()) {
    if ((aud.resolvedPersonIds ?? []).includes(personId)) {
      subjectAudienceIds.add(aud.id);
    }
  }
  return {
    audiencesById,
    personalAudienceId: personalId,
    subjectPersonId: personId,
    subjectRole,
    subjectAudienceIds,
  };
}

// ── entry point ──────────────────────────────────────────────────────────

export async function loadAttendeeReport(
  eventId: string,
  personId: string,
): Promise<AttendeeReportData> {
  const [personSnap, profileSnap, participantSnap] = await Promise.all([
    getDoc(doc(db, "persons", personId)),
    getDoc(doc(db, "personProfiles", personId)),
    getDoc(doc(db, "eventParticipants", `${eventId}_${personId}`)),
  ]);

  if (!personSnap.exists()) {
    throw new Error(`Person ${personId} not found`);
  }

  const person = { id: personSnap.id, ...personSnap.data() } as Person;
  const profile = profileSnap.exists()
    ? ({ id: profileSnap.id, ...profileSnap.data() } as PersonProfile)
    : null;
  const participant = participantSnap.exists()
    ? ({
        id: participantSnap.id,
        ...participantSnap.data(),
      } as EventParticipant)
    : null;

  const subjectRole: Role | null =
    participant?.role ?? (person.role ?? null);
  const personalId = personalAudienceId(eventId, personId);

  // Subject audience set: every audience whose resolved roster includes
  // this person. Works for claimed AND pending persons because
  // resolvedPersonIds is populated by the cascade regardless of claim
  // status (ADR-137).
  const audiencesColl = collection(db, "audiences");
  const audSnap = await getDocs(
    query(
      audiencesColl,
      where("eventId", "==", eventId),
      where("resolvedPersonIds", "array-contains", personId),
    ),
  );
  const audiences = audSnap.docs.map(
    (d) => ({ id: d.id, ...(d.data() as Omit<Audience, "id">) }) as Audience,
  );

  // The personal audience may not be in the cascade query yet for a
  // never-targeted pending person — load it explicitly so it's available
  // for trace attribution.
  if (!audiences.some((a) => a.id === personalId)) {
    const personalSnap = await getDoc(doc(db, "audiences", personalId));
    if (personalSnap.exists()) {
      audiences.push({
        id: personalSnap.id,
        ...personalSnap.data(),
      } as Audience);
    }
  }

  const subjectAudienceIds = unique(audiences.map((a) => a.id));

  // Fetch consumer docs by audience match. Every consumer is
  // audience-keyed; the linked audience's resolvedPersonIds[] is the
  // single source of truth for who can read each consumer (ADR-137).
  const [sessions, travelItems, audienceMatchedNotifications, receiptSnap] =
    await Promise.all([
      fetchByEventAndAudiences<Session>(
        "sessions",
        eventId,
        subjectAudienceIds,
      ),
      fetchByEventAndAudiences<TravelItem>(
        "travelItems",
        eventId,
        subjectAudienceIds,
      ),
      fetchByEventAndAudiences<Notification>(
        "notifications",
        eventId,
        subjectAudienceIds,
      ),
      getDocs(
        query(
          collection(db, "notificationReceipts"),
          where("personId", "==", personId),
          where("eventId", "==", eventId),
        ),
      ),
    ]);

  const receipts = receiptSnap.docs.map(
    (d) =>
      ({
        id: d.id,
        ...(d.data() as Omit<NotificationReceipt, "id">),
      }) as NotificationReceipt,
  );

  // Notifications: union of audience-matched + any whose receipt exists
  // for this person (catches edge cases where a receipt's audience was
  // re-resolved after send).
  const receiptNotificationIds = unique(receipts.map((r) => r.notificationId));
  const receiptNotifications = await fetchDocsByIdsChunked<Notification>(
    "notifications",
    receiptNotificationIds,
  );

  const notificationsById = new Map<string, Notification>();
  for (const n of audienceMatchedNotifications)
    notificationsById.set(n.id, n);
  for (const n of receiptNotifications) notificationsById.set(n.id, n);

  const receiptByNotifId = new Map<string, NotificationReceipt>();
  for (const r of receipts) receiptByNotifId.set(r.notificationId, r);

  const isClaimed = person.authUid !== null;
  const notifications: ReportNotificationEntry[] = Array.from(
    notificationsById.values(),
  ).map((notification) => {
    const receipt = receiptByNotifId.get(notification.id) ?? null;
    let deliveryState: ReportNotificationEntry["deliveryState"];
    if (receipt) {
      deliveryState = "delivered";
    } else if (notification.status === "sent") {
      deliveryState = isClaimed ? "queued" : "pending_activation";
    } else {
      deliveryState = "queued";
    }
    return { notification, receipt, deliveryState };
  });

  const notificationIds = unique(Array.from(notificationsById.keys()));
  const surveys = await fetchSurveysByNotificationIds(eventId, notificationIds);

  const audiencesById = new Map<string, Audience>();
  for (const a of audiences) audiencesById.set(a.id, a);
  await backfillReferencedAudiences(
    [...sessions, ...travelItems, ...Array.from(notificationsById.values())],
    audiencesById,
  );

  const traceIndex = buildTraceIndex(
    audiencesById,
    personalId,
    personId,
    subjectRole,
  );

  return {
    subject: { personId, person, profile, participant, isClaimed },
    audiences,
    sessions,
    travelItems,
    notifications,
    surveys,
    traceIndex,
  };
}
